import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";
import "./sip-calculator.css";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

const formatINR = (amount) =>
  Number(amount.toFixed(0)).toLocaleString("en-IN");

function computeEMI(loanAmount, rate, years) {
  if (loanAmount <= 0 || rate <= 0 || years <= 0) return null;

  const n = years * 12;
  const r = rate / (12 * 100);

  const emi =
    (loanAmount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);

  const totalPayment = emi * n;
  const totalInterest = totalPayment - loanAmount;

  let balance = loanAmount;
  const breakdown = [];
  for (let year = 1; year <= years; year++) {
    let interestPaid = 0;
    let principalPaid = 0;

    for (let month = 1; month <= 12; month++) {
      const interest = balance * r;
      const principal = emi - interest;
      balance -= principal;
      interestPaid += interest;
      principalPaid += principal;
      if (balance <= 0) break;
    }

    breakdown.push({
      year,
      principal: Number(principalPaid.toFixed(0)),
      interest: Number(interestPaid.toFixed(0)),
    });

    if (balance <= 0) break;
  }

  return {
    emi: Number(emi.toFixed(0)),
    totalPayment: Number(totalPayment.toFixed(0)),
    totalInterest: Number(totalInterest.toFixed(0)),
    breakdown,
  };
}

export default function EMICalculator() {
  const [loanAmount, setLoanAmount] = useState(500000);
  const [rate, setRate] = useState(9);
  const [years, setYears] = useState(5);

  const results = useMemo(
    () => computeEMI(loanAmount, rate, years),
    [loanAmount, rate, years]
  );

  const doughnutData = results && {
    labels: ["Principal Amount", "Interest Payable"],
    datasets: [
      {
        data: [loanAmount, results.totalInterest],
        backgroundColor: ["#139bb2", "#a8e4ec"],
        borderWidth: 0,
        hoverOffset: 4,
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "68%",
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 16,
          color: "#64748b",
          font: { size: 12, weight: "600" },
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ₹${formatINR(ctx.parsed)}`,
        },
      },
    },
  };

  const barData = results && {
    labels: results.breakdown.map((d) => `Y${d.year}`),
    datasets: [
      {
        label: "Principal Repaid",
        data: results.breakdown.map((d) => d.principal),
        backgroundColor: "#139bb2",
        borderRadius: 4,
        stack: "repay",
      },
      {
        label: "Interest Paid",
        data: results.breakdown.map((d) => d.interest),
        backgroundColor: "#a8e4ec",
        borderRadius: 4,
        stack: "repay",
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ₹${formatINR(ctx.parsed.y)}`,
        },
      },
      legend: {
        position: "top",
        align: "end",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          color: "#64748b",
          font: { size: 12, weight: "600" },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { color: "#94a3b8", font: { size: 11 } },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: "#eef2f5" },
        ticks: {
          color: "#94a3b8",
          font: { size: 11 },
          callback: (v) => `₹${formatINR(v)}`,
        },
      },
    },
    interaction: { mode: "index", intersect: false },
  };

  return (
    <div className="sip-page">
      <section className="sip-hero">
        <div className="sip-hero-blob" aria-hidden="true" />
        <div className="sip-hero-inner">
          <p className="sip-eyebrow">EMI CALCULATOR</p>
          <h1 className="sip-hero-title">
            Know Your Monthly EMI Before You <span>Borrow</span>
          </h1>
          <p className="sip-hero-lead">
            Estimate your monthly loan repayment, total interest, and overall
            cost. Adjust amount, rate, and tenure to plan with clarity.
          </p>
        </div>
      </section>

      <section className="sip-calc">
        <div className="sip-calc-inner">
          <div className="sip-calc-grid">
            <div className="sip-panel">
              <h2 className="sip-panel-title">Loan Details</h2>

              <div className="sip-field">
                <div className="sip-field-head">
                  <label htmlFor="emi-amount">Loan Amount</label>
                  <div className="sip-field-value sip-field-value--wide">
                    <span>₹</span>
                    <input
                      id="emi-amount"
                      type="number"
                      min={10000}
                      max={10000000}
                      step={10000}
                      value={loanAmount}
                      onChange={(e) =>
                        setLoanAmount(Number(e.target.value) || 0)
                      }
                    />
                  </div>
                </div>
                <input
                  type="range"
                  className="sip-range"
                  min={10000}
                  max={5000000}
                  step={10000}
                  value={Math.min(loanAmount, 5000000)}
                  onChange={(e) => setLoanAmount(Number(e.target.value))}
                  aria-label="Loan amount slider"
                />
                <div className="sip-range-ends">
                  <span>₹10,000</span>
                  <span>₹50,00,000</span>
                </div>
              </div>

              <div className="sip-field">
                <div className="sip-field-head">
                  <label htmlFor="emi-rate">Interest Rate (p.a.)</label>
                  <div className="sip-field-value">
                    <input
                      id="emi-rate"
                      type="number"
                      min={1}
                      max={30}
                      step={0.1}
                      value={rate}
                      onChange={(e) => setRate(Number(e.target.value) || 0)}
                    />
                    <span>%</span>
                  </div>
                </div>
                <input
                  type="range"
                  className="sip-range"
                  min={1}
                  max={30}
                  step={0.1}
                  value={rate}
                  onChange={(e) => setRate(Number(e.target.value))}
                  aria-label="Interest rate slider"
                />
                <div className="sip-range-ends">
                  <span>1%</span>
                  <span>30%</span>
                </div>
              </div>

              <div className="sip-field">
                <div className="sip-field-head">
                  <label htmlFor="emi-years">Loan Tenure</label>
                  <div className="sip-field-value">
                    <input
                      id="emi-years"
                      type="number"
                      min={1}
                      max={30}
                      step={1}
                      value={years}
                      onChange={(e) => setYears(Number(e.target.value) || 0)}
                    />
                    <span>Yr</span>
                  </div>
                </div>
                <input
                  type="range"
                  className="sip-range"
                  min={1}
                  max={30}
                  step={1}
                  value={years}
                  onChange={(e) => setYears(Number(e.target.value))}
                  aria-label="Loan tenure slider"
                />
                <div className="sip-range-ends">
                  <span>1 Yr</span>
                  <span>30 Yr</span>
                </div>
              </div>

              <div className="sip-cta-row">
                <Link to="/loan" className="sip-btn sip-btn--primary">
                  Explore Loan Options
                </Link>
                <Link to="/contact" className="sip-btn sip-btn--ghost">
                  Talk to an Advisor
                </Link>
              </div>
            </div>

            <div className="sip-panel sip-panel--results">
              <h2 className="sip-panel-title">EMI Breakdown</h2>

              {results ? (
                <>
                  <div className="sip-results-top">
                    <div className="sip-donut-wrap">
                      <Doughnut data={doughnutData} options={doughnutOptions} />
                    </div>
                    <div className="sip-stats">
                      <div className="sip-stat sip-stat--total">
                        <span className="sip-stat-label">Monthly EMI</span>
                        <strong>₹{formatINR(results.emi)}</strong>
                      </div>
                      <div className="sip-stat">
                        <span className="sip-stat-label">
                          Total Interest Payable
                        </span>
                        <strong className="sip-stat--teal">
                          ₹{formatINR(results.totalInterest)}
                        </strong>
                      </div>
                      <div className="sip-stat">
                        <span className="sip-stat-label">Total Payment</span>
                        <strong>₹{formatINR(results.totalPayment)}</strong>
                      </div>
                    </div>
                  </div>

                  <p className="sip-summary">
                    For a loan of <strong>₹{formatINR(loanAmount)}</strong> at{" "}
                    <strong>{rate}%</strong> p.a. over{" "}
                    <strong>
                      {years} {years > 1 ? "years" : "year"}
                    </strong>
                    , your monthly EMI will be{" "}
                    <strong>₹{formatINR(results.emi)}</strong>. You will pay{" "}
                    <strong>₹{formatINR(results.totalInterest)}</strong> as
                    interest, for a total payment of{" "}
                    <strong>₹{formatINR(results.totalPayment)}</strong>.
                  </p>
                </>
              ) : (
                <p className="sip-empty">
                  Enter valid positive values to see your EMI projection.
                </p>
              )}
            </div>
          </div>

          {results && (
            <div className="sip-panel sip-panel--chart">
              <div className="sip-chart-head">
                <h2 className="sip-panel-title">Yearly Breakdown</h2>
                <p>Principal repaid vs interest paid each year</p>
              </div>
              <div className="sip-bar-wrap">
                <Bar data={barData} options={barOptions} />
              </div>
            </div>
          )}

          <div className="sip-related">
            <p>Try our other calculators</p>
            <div className="sip-related-links">
              <Link to="/SipCalculator">SIP</Link>
              <Link to="/LumpsumCalculator">Lumpsum</Link>
              <Link to="/RetirementCalculator">Retirement</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
